/**
 * V3.1p — Page Admin AMO (Assistants à Maîtrise d'Ouvrage).
 *
 * CRUD complet des AMO. Chaque AMO a une liste de zones (préfixes CP ou
 * départements). Les dossiers MaPrimeAdapt' créés par la sync Meta Ads
 * reçoivent automatiquement l'AMO dont une zone matche le code postal du
 * bénéficiaire.
 *
 * Endpoints : /api/cee/amos (GET POST / {id} PUT DELETE / _resolve/{cp} GET)
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  UserPlus, Pencil, Trash2, Users, Loader2, Search, MapPin, CheckCircle2,
  XCircle, Mail, Phone, Building2, X, Eye, FolderOpen, ExternalLink, StickyNote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Link } from 'react-router-dom';

const EMPTY_FORM = {
  nom: '', societe: '', email: '', telephone: '', siret: '',
  adresse: '', cp: '', ville: '',
  zonesText: '',  // saisie utilisateur, splittée au submit
  active: true, notes: '',
};

export default function AdminAmos() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cpFilter, setCpFilter] = useState('');

  // Form state (create / edit)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [toDelete, setToDelete] = useState(null);

  // Dialog détail AMO (notes + dossiers attribués)
  const [detailAmo, setDetailAmo] = useState(null);  // amo object
  const [detailDossiers, setDetailDossiers] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (amo) => {
    setDetailAmo(amo);
    setDetailDossiers([]);
    setDetailLoading(true);
    try {
      const r = await api.get(`/cee/amos/${amo.amo_id}/dossiers`);
      setDetailDossiers(r.data?.items || []);
    } catch (e) {
      toast.error('Impossible de charger les dossiers');
    } finally { setDetailLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (cpFilter) params.cp = cpFilter.replace(/\D/g, '');
      const r = await api.get('/cee/amos', { params });
      setList(r.data?.items || []);
    } catch (e) {
      toast.error('Impossible de charger les AMO');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (amo) => {
    setEditingId(amo.amo_id);
    setForm({
      nom: amo.nom || '',
      societe: amo.societe || '',
      email: amo.email || '',
      telephone: amo.telephone || '',
      siret: amo.siret || '',
      adresse: amo.adresse || '',
      cp: amo.cp || '',
      ville: amo.ville || '',
      zonesText: (amo.zones || []).join(', '),
      active: amo.active !== false,
      notes: amo.notes || '',
    });
    setDialogOpen(true);
  };

  const parseZones = (text) =>
    (text || '')
      .split(/[,\s;]+/)
      .map((z) => (z || '').trim())
      .filter(Boolean);

  const submit = async () => {
    const nom = form.nom.trim();
    const email = form.email.trim();
    const zones = parseZones(form.zonesText);
    if (!nom) return toast.error('Nom requis');
    if (!email) return toast.error('Email requis');
    if (zones.length === 0) return toast.error('Au moins une zone d\'intervention');

    const payload = {
      nom, societe: form.societe.trim() || null, email,
      telephone: form.telephone.trim() || null,
      siret: form.siret.trim() || null,
      adresse: form.adresse.trim() || null,
      cp: form.cp.trim() || null,
      ville: form.ville.trim() || null,
      zones, active: !!form.active,
      notes: form.notes.trim() || null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/cee/amos/${editingId}`, payload);
        toast.success(`AMO "${nom}" mis à jour`);
      } else {
        await api.post('/cee/amos', payload);
        toast.success(`AMO "${nom}" créé`);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec enregistrement');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/cee/amos/${toDelete.amo_id}`);
      toast.success(`AMO "${toDelete.nom}" supprimé`);
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec suppression');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-amos-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-600" />
            Assistants à Maîtrise d'Ouvrage (AMO)
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Référencez vos AMO et leurs zones d'intervention. Chaque dossier MaPrimeAdapt'
            créé par la sync Meta Ads est automatiquement attribué à l'AMO dont une zone
            matche le code postal du bénéficiaire.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700"
                data-testid="amo-create-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Nouvel AMO
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="amo-search" className="text-xs">Recherche</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              id="amo-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom, société, email…"
              className="pl-9"
              data-testid="amo-search-input"
            />
          </div>
        </div>
        <div className="w-32">
          <Label htmlFor="amo-cp-filter" className="text-xs">
            Tester un CP
          </Label>
          <Input
            id="amo-cp-filter"
            value={cpFilter}
            onChange={(e) => setCpFilter(e.target.value)}
            placeholder="75001"
            maxLength={5}
            data-testid="amo-cp-filter"
          />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}
                data-testid="amo-reload-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading && list.length === 0 ? (
          <div className="p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm">Aucun AMO référencé</p>
            <p className="text-xs text-slate-400 mt-1">
              Cliquez sur "Nouvel AMO" pour commencer.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">AMO</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Contact</th>
                <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Zones</th>
                <th className="text-center px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Statut</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((amo) => (
                <tr key={amo.amo_id} className="hover:bg-slate-50 cursor-pointer"
                    data-testid={`amo-row-${amo.amo_id}`}
                    onClick={() => openDetail(amo)}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                      {amo.nom}
                      {amo.notes && (
                        <StickyNote className="w-3 h-3 text-amber-500"
                                     title="Note interne disponible" />
                      )}
                    </div>
                    {amo.societe && (
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3" /> {amo.societe}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="flex items-center gap-1.5 text-slate-700">
                      <Mail className="w-3 h-3" /> {amo.email}
                    </div>
                    {amo.telephone && (
                      <div className="flex items-center gap-1.5 text-slate-500 mt-0.5">
                        <Phone className="w-3 h-3" /> {amo.telephone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[300px]">
                      {(amo.zones || []).map((z) => (
                        <span key={z}
                              className="inline-flex items-center gap-0.5 text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                          <MapPin className="w-2.5 h-2.5" />{z}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {amo.active ? (
                      <Badge className="bg-green-100 text-green-700 border-0">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Actif
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 border-0">
                        <XCircle className="w-3 h-3 mr-1" /> Inactif
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openDetail(amo)}
                            data-testid={`amo-view-${amo.amo_id}`}
                            title="Voir dossiers attribués">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(amo)}
                            data-testid={`amo-edit-${amo.amo_id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm"
                            onClick={() => setToDelete(amo)}
                            className="text-red-600 hover:bg-red-50"
                            data-testid={`amo-delete-${amo.amo_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="amo-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Modifier l\'AMO' : 'Nouvel AMO'}
            </DialogTitle>
            <DialogDescription>
              Un AMO sera attribué automatiquement aux nouveaux dossiers
              MaPrimeAdapt' dont le code postal matche l'une de ses zones.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="f-nom">Nom *</Label>
                <Input id="f-nom" value={form.nom}
                       onChange={(e) => setForm({ ...form, nom: e.target.value })}
                       placeholder="Jean Dupont" data-testid="f-nom" />
              </div>
              <div>
                <Label htmlFor="f-societe">Société</Label>
                <Input id="f-societe" value={form.societe}
                       onChange={(e) => setForm({ ...form, societe: e.target.value })}
                       placeholder="Habitat Conseil" data-testid="f-societe" />
              </div>
            </div>

            <div>
              <Label htmlFor="f-email">Email *</Label>
              <Input id="f-email" type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     placeholder="contact@amo.fr" data-testid="f-email" />
            </div>
            <div>
              <Label htmlFor="f-tel">Téléphone</Label>
              <Input id="f-tel" value={form.telephone}
                     onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                     placeholder="0145678901" data-testid="f-tel" />
            </div>

            <div>
              <Label htmlFor="f-siret">SIRET</Label>
              <Input id="f-siret" value={form.siret}
                     onChange={(e) => setForm({ ...form, siret: e.target.value })}
                     placeholder="123 456 789 00012" />
            </div>
            <div>
              <Label htmlFor="f-cp">CP · Ville</Label>
              <div className="flex gap-2">
                <Input id="f-cp" value={form.cp}
                       onChange={(e) => setForm({ ...form, cp: e.target.value })}
                       className="w-24" placeholder="75001" />
                <Input value={form.ville}
                       onChange={(e) => setForm({ ...form, ville: e.target.value })}
                       className="flex-1" placeholder="Paris" />
              </div>
            </div>

            <div className="col-span-2">
              <Label htmlFor="f-adresse">Adresse</Label>
              <Input id="f-adresse" value={form.adresse}
                     onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                     placeholder="10 rue de la Paix" />
            </div>

            <div className="col-span-2">
              <Label htmlFor="f-zones">
                Zones d'intervention <span className="text-red-500">*</span>
              </Label>
              <Input id="f-zones" value={form.zonesText}
                     onChange={(e) => setForm({ ...form, zonesText: e.target.value })}
                     placeholder="75, 77, 93, 94, 97200"
                     data-testid="f-zones" />
              <p className="text-xs text-slate-500 mt-1">
                Préfixes de codes postaux ou départements séparés par des virgules
                (ex: <code className="bg-slate-100 px-1 rounded">75</code> couvre Paris,
                {' '}<code className="bg-slate-100 px-1 rounded">75001</code> couvre
                uniquement le 1er arrondissement). Match par préfixe le plus spécifique.
              </p>
            </div>

            <div className="col-span-2">
              <Label htmlFor="f-notes">Notes internes</Label>
              <Textarea id="f-notes" value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        rows={3} maxLength={2000}
                        placeholder="Spécialité, horaires, conditions particulières…" />
            </div>

            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.active}
                      onCheckedChange={(v) => setForm({ ...form, active: v })}
                      id="f-active" />
              <Label htmlFor="f-active">
                AMO actif (reçoit les attributions automatiques)
              </Label>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}
                    disabled={saving}>
              <X className="w-4 h-4 mr-2" /> Annuler
            </Button>
            <Button onClick={submit} disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="amo-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingId ? 'Enregistrer' : 'Créer l\'AMO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog détail AMO : notes + dossiers attribués */}
      <Dialog open={!!detailAmo} onOpenChange={(v) => !v && setDetailAmo(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto"
                        data-testid="amo-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              {detailAmo?.nom}
              {detailAmo?.societe && (
                <span className="text-sm font-normal text-slate-500">
                  — {detailAmo.societe}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-3 flex-wrap text-xs">
              {detailAmo?.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {detailAmo.email}
                </span>
              )}
              {detailAmo?.telephone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {detailAmo.telephone}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Zones :{' '}
                {(detailAmo?.zones || []).join(', ') || '—'}
              </span>
            </DialogDescription>
          </DialogHeader>

          {/* Note interne */}
          {detailAmo?.notes && (
            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4"
                  data-testid="amo-detail-notes">
              <div className="flex items-center gap-2 mb-1.5">
                <StickyNote className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-amber-900 text-sm">
                  Note interne
                </h3>
              </div>
              <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">
                {detailAmo.notes}
              </p>
            </div>
          )}

          {/* Dossiers attribués */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-slate-600" />
                Dossiers attribués
                <Badge variant="outline" className="ml-1 bg-slate-50">
                  {detailDossiers.length}
                </Badge>
              </h3>
            </div>

            {detailLoading ? (
              <div className="py-8 text-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
              </div>
            ) : detailDossiers.length === 0 ? (
              <div className="py-8 text-center bg-slate-50 rounded-lg border border-slate-200">
                <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Aucun dossier attribué à cet AMO
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Les nouveaux dossiers MaPrimeAdapt' dont le CP matche
                  seront automatiquement attribués ici.
                </p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase">Dossier</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase">Bénéficiaire</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase">CP</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase">Statut</th>
                      <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase">Attribution</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detailDossiers.map((d) => (
                      <tr key={d.dossier_id} className="hover:bg-slate-50"
                          data-testid={`amo-dossier-row-${d.dossier_id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">
                          {d.dossier_id.slice(-10)}
                          <div className="mt-0.5">
                            {d.aide_type === 'maprimeadapt' ? (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                                MPA
                              </span>
                            ) : (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">
                                CEE
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-800">
                          <div className="font-medium">{d.beneficiaire_nom || '—'}</div>
                          {d.beneficiaire_telephone && (
                            <div className="text-xs text-slate-500">
                              {d.beneficiaire_telephone}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">
                          {d.beneficiaire_cp || '—'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">
                            {d.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-[10px]">
                          {d.amo_auto_assigned ? (
                            <span className="inline-flex items-center gap-0.5 text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                              Auto
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                              Manuel
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link
                            to={`/cee/dossiers/${d.dossier_id}`}
                            className="inline-flex items-center gap-1 text-xs text-[#1C49A6] hover:underline"
                            onClick={() => setDetailAmo(null)}
                          >
                            Ouvrir <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDetailAmo(null)}>
              Fermer
            </Button>
            <Button variant="outline" onClick={() => {
              const amo = detailAmo;
              setDetailAmo(null);
              setTimeout(() => openEdit(amo), 100);
            }}>
              <Pencil className="w-4 h-4 mr-2" /> Modifier l'AMO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet AMO ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'AMO <b>{toDelete?.nom}</b> sera supprimé. Les dossiers déjà
              attribués conservent leur AMO (champ figé) mais aucune nouvelle
              attribution automatique ne se fera.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}
                               className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
